import { ConnectedPosition, Overlay, OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal } from '@angular/core';
import { ProvisionalStatus } from '@ffd/api-client';
import { HighlightDirective, TooltipModule } from '@libs/ui-lib';

import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CdkMenuModule } from '@angular/cdk/menu';
import { animate, AnimationTriggerMetadata, style, transition, trigger } from '@angular/animations';
import { TagEvent, TagPusherService, ToastService } from '@libs/shared-services';
import { Clipboard } from '@angular/cdk/clipboard';
import { FfdNoteItem, FfdNotesScreenService } from '../ffd-notes-screen';

const TITLE_LENGTH_THRESHOLD = 70;
const animationTiming = '150ms cubic-bezier(0.4, 0, 0.2, 1)';

export const expandAnimation: AnimationTriggerMetadata = trigger('expandAnimation', [
  transition('expanded => collapsed', [style({ height: '*' }), animate(animationTiming, style({ height: 0, overflow: 'hidden' }))]),
  transition('collapsed => expanded', [style({ height: 0, overflow: 'hidden' }), animate(animationTiming, style({ height: '*' }))]),
]);

@Component({
  selector: 'lib-ffd-note-item',
  imports: [CommonModule, TranslateModule, SvgIconComponent, HighlightDirective, OverlayModule, TooltipModule, CdkMenuModule],
  templateUrl: './ffd-note-item.component.html',
  styleUrl: './ffd-note-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FfdNoteItemComponent implements AfterViewInit {
  private readonly elementRef = inject(ElementRef);
  private readonly overlay = inject(Overlay);
  private readonly toastService = inject(ToastService);
  private readonly clipboard = inject(Clipboard);
  private readonly translateService = inject(TranslateService);
  private readonly tagPusherService = inject(TagPusherService);
  private readonly ffdNotesScreenService = inject(FfdNotesScreenService);

  public noteTitle = input<string | undefined>();
  public hasBeenCopied = input<boolean>();
  public date = input.required<string>();
  public searchTerm = input<string>('');
  public isLocked = input.required<boolean>();
  public isProvisional = input.required<boolean>();
  public expandable = input<boolean>(false);
  public noteContent = input.required<string>();
  public maxHeight = input<number | undefined>();
  public noteId = input<string>();
  public inEditMode = input<boolean>(false);
  public hideMenu = input<boolean>(false);
  public provisionalStatus = input<ProvisionalStatus | undefined>();
  public disabled = input<boolean>(false);
  public noteClosed = output<HTMLElement>();
  public editNote = output<void>();
  public deleteNote = output<void>();

  protected isExpanded = false;
  protected isAnimating = signal(false);
  protected strippedNoteContent = computed(() => this.noteContent()?.replace(/<[^>]*>/g, '') ?? '');
  public dateTimeFormat = 'h:mm a';
  protected ProvisionalStatus = ProvisionalStatus;
  protected isExpandableNote = false;

  public readonly dropdownOpen = signal(false);
  public readonly overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'center',
    },
  ];
  public readonly scrollStrategy = this.overlay.scrollStrategies.noop();

  public ngAfterViewInit(): void {
    if (this.expandable()) {
      // only in charting
      // this is a workaround with exact length, dynamic title height calculation is misleading with clamp
      const title = this.noteTitle();
      this.isExpandableNote = title ? title.length > TITLE_LENGTH_THRESHOLD : false;
    }
  }

  protected setNoteExpandAnimationState(isAnimating: boolean): void {
    this.isAnimating.set(isAnimating);
  }

  protected toggleExpandedNote(): void {
    this.isExpanded = !this.isExpanded;
  }

  protected closeExpandedNote(): void {
    this.isExpanded = false;
    this.elementRef.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  protected onMenuClick(event: Event): void {
    event.stopPropagation();
    if (!this.disabled()) {
      this.toggleDropdown();
    }
  }

  protected toggleDropdown(): void {
    this.dropdownOpen.set(!this.dropdownOpen());
  }

  protected onEditNote(): void {
    this.dropdownOpen.set(false);
    this.editNote.emit();
  }

  protected onDeleteNote(): void {
    this.dropdownOpen.set(false);
    this.deleteNote.emit();
  }

  protected onCopyNote(): void {
    const noteContent = this.strippedNoteContent();
    if (noteContent) {
      this.tagPusherService.pushTag({
        event: TagEvent.FfdNotesNoteCopied,
      });
      this.clipboard.copy(noteContent);
      this.toastService.success(this.translateService.instant('express_notes_note_copied_toast'), undefined, 'auto');
    }
    this.toggleDropdown();

    // send request to update hasBeenCopied to true
    if (this.noteId() === undefined) {
      return;
    }

    const noteToUpdate: FfdNoteItem = {
      id: this.noteId()!,
      title: this.noteTitle(),
      content: this.noteContent(),
      date: this.date(),
      hasBeenCopied: true,
    };

    this.ffdNotesScreenService.updateNote(noteToUpdate);
  }
}
